import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Clock, Wrench, Phone, MapPin, Calendar, ShieldCheck } from "lucide-react";

const STATUS_VIEW = {
  received: { label: "Trotineta primita", icon: Wrench, color: "text-blue-400", desc: "Trotineta ta a ajuns la noi si urmeaza sa fie evaluata." },
  in_progress: { label: "In lucru", icon: Clock, color: "text-amber-400", desc: "Lucram la trotineta ta in atelier." },
  completed: { label: "Finalizata - poate fi ridicata", icon: CheckCircle2, color: "text-green-400", desc: "Trotineta ta este gata! Te asteptam sa o ridici." },
  delivered: { label: "Predata", icon: CheckCircle2, color: "text-neutral-400", desc: "Trotineta a fost predata. Multumim!" },
};

export default function PublicRepairStatus() {
  const { ticket } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/repair/${ticket}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [ticket]);

  if (err) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
      <div className="text-center">
        <h1 className="font-display text-4xl uppercase">Ticket inexistent</h1>
        <p className="text-neutral-400 mt-3">Verifica codul de pe foaie sau contacteaza-ne.</p>
        <a href="tel:0743110277" className="inline-block mt-6 bg-[#00E5FF] text-black px-6 py-3 uppercase tracking-wider text-sm font-semibold">Suna 0743 110 277</a>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white text-center py-24">Se incarca...</div>;

  const view = STATUS_VIEW[data.status] || STATUS_VIEW.received;
  const Icon = view.icon;

  return (
    <div data-testid="public-status-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-neutral-900 py-5 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 bg-[#00E5FF] flex items-center justify-center text-black font-bold">K</div>
          <div className="leading-none">
            <div className="font-display text-base">TROTI-X SERVICE</div>
            <div className="text-[9px] tracking-[0.3em] text-[#00E5FF] uppercase mt-1">Status reparatie</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-3">// Ticket #{data.ticket}</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none">{data.scooter_model}</h1>
        <p className="text-sm text-neutral-400 mt-2">Primita pe {new Date(data.created_at).toLocaleDateString("ro-RO")}</p>

        <div className={`mt-8 border border-neutral-800 bg-neutral-950 p-6 ${data.status === "completed" ? "border-green-500/60" : ""}`}>
          <div className="flex items-start gap-4">
            <Icon className={`w-12 h-12 ${view.color}`} />
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-neutral-500">Status actual</div>
              <h2 className="font-display text-2xl uppercase mt-1">{view.label}</h2>
              <p className="text-sm text-neutral-300 mt-2">{view.desc}</p>
              {data.completed_at && (
                <p className="text-xs text-neutral-500 mt-2">Finalizata pe {new Date(data.completed_at).toLocaleString("ro-RO")}</p>
              )}
            </div>
          </div>

          {data.status === "completed" && (
            <div className="mt-6 bg-[#00E5FF]/10 border border-[#00E5FF]/40 p-4">
              <div className="text-xs uppercase tracking-widest text-[#00E5FF] font-bold mb-1">Atentie</div>
              <p className="text-sm text-neutral-200">Dupa <strong>7 zile</strong> de la finalizare se percepe o taxa de <strong>10 RON / zi</strong> stationare.</p>
            </div>
          )}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <div className="bg-neutral-950 border border-neutral-800 p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Problema raportata</div>
            <p className="text-sm text-white mt-2">{data.issue_description}</p>
          </div>
          <div className="bg-neutral-950 border border-neutral-800 p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Cost</div>
            <div className="mt-2 text-sm">
              {data.estimated_cost ? <div>Estimat: <span className="text-white">{data.estimated_cost} RON</span></div> : null}
              {data.final_cost != null ? <div className="text-[#00E5FF] font-display text-2xl mt-1">{data.final_cost} RON</div> : <div className="text-neutral-500 text-xs">Cost final disponibil dupa finalizare</div>}
            </div>
          </div>
        </div>

        {data.completion_notes && (
          <div className="mt-4 bg-neutral-950 border border-neutral-800 p-5">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Note service</div>
            <p className="text-sm text-white mt-2">{data.completion_notes}</p>
          </div>
        )}

        {data.warranty_months > 0 && data.warranty_until && (() => {
          const expired = new Date(data.warranty_until) < new Date();
          return (
            <div data-testid="public-warranty-card" className={`mt-4 border p-5 ${expired ? "border-neutral-800 bg-neutral-950" : "border-green-500/50 bg-green-950/20"}`}>
              <div className="flex items-start gap-3">
                <ShieldCheck className={`w-8 h-8 ${expired ? "text-neutral-500" : "text-green-400"}`} />
                <div>
                  <div className={`text-xs uppercase tracking-widest font-bold ${expired ? "text-neutral-500" : "text-green-400"}`}>
                    Garantie lucrare · {data.warranty_months} {data.warranty_months === 1 ? "luna" : "luni"}
                  </div>
                  {data.warranty_items && <p className="text-sm text-white mt-1.5">{data.warranty_items}</p>}
                  <p className="text-xs text-neutral-400 mt-1">
                    {expired ? `Garantia a expirat pe ${new Date(data.warranty_until).toLocaleDateString("ro-RO")}` : `Valabila pana la ${new Date(data.warranty_until).toLocaleDateString("ro-RO")} — pastreaza certificatul de garantie primit.`}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="mt-10 border border-neutral-800 p-6">
          <h3 className="font-display text-lg uppercase mb-4">Vino sa ridici</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> Bd. Liviu Rebreanu 104A, Timisoara</li>
            <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> <a href="tel:0743110277" className="hover:text-[#00E5FF]">0743 110 277</a></li>
            <li className="flex items-start gap-3"><Calendar className="w-4 h-4 mt-0.5 text-[#00E5FF]" /> L-V 9:00-17:00 · Sambata 9:00-14:00</li>
          </ul>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#00E5FF]">← Inapoi la site</Link>
        </div>
      </main>
    </div>
  );
}
